import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CreditCard, FileText, Globe, Map as MapIcon, Pencil, Plug, Users } from 'lucide-react';
import type { RoutySetupLinkArtifact, RoutySetupPage } from '../../business/routy/routyArtifacts';

interface SetupLinkCardProps {
  artifact: RoutySetupLinkArtifact;
  /** Called after navigating, so the panel can get out of the page's way. */
  onOpened?: () => void;
}

const SETUP_PAGES: Record<
  RoutySetupPage,
  { label: string; icon: React.ComponentType<{ className?: string }>; path: (artifact: RoutySetupLinkArtifact) => string }
> = {
  billing: { label: 'Plans & Billing', icon: CreditCard, path: () => '/billing' },
  team: { label: 'Team Members', icon: Users, path: () => '/settings?section=team' },
  integrations: { label: 'Integrations', icon: Plug, path: () => '/settings?section=integrations' },
  branding: { label: 'Business & Branding', icon: Globe, path: () => '/settings?section=branding' },
  territories: { label: 'Route Preferences', icon: MapIcon, path: () => '/settings?section=route-preferences' },
  booking_forms: { label: 'Booking Forms', icon: FileText, path: () => '/booking-forms' },
  form_editor: {
    label: 'Form editor',
    icon: Pencil,
    path: (artifact) =>
      artifact.bookingFormId ? `/booking-forms?formId=${encodeURIComponent(artifact.bookingFormId)}` : '/booking-forms',
  },
};

/** A hand-off to the settings screen for something Routy can't do in chat (`open_setup_page`). */
const SetupLinkCard: React.FC<SetupLinkCardProps> = ({ artifact, onOpened }) => {
  const navigate = useNavigate();
  const page = SETUP_PAGES[artifact.page];
  if (!page) return null;
  const Icon = page.icon;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <Icon className="w-4 h-4 text-accent-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-ink-900 truncate">{page.label}</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.reason && <p className="text-xs text-ink-600 leading-snug">{artifact.reason}</p>}
        {/* eslint-disable-next-line no-restricted-syntax -- Accent full-width CTA matching the other Routy cards; Button's primary is ink-900 */}
        <button
          type="button"
          onClick={() => {
            navigate(page.path(artifact));
            onOpened?.();
          }}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-accent-600 text-white text-xs font-semibold hover:bg-accent-700 active:scale-[0.99] transition-all"
        >
          Open {page.label}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default SetupLinkCard;
